import { useEffect, useMemo } from "react";
import { Box, Button, Modal, Skeleton, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import { keyframes } from "@emotion/react";
import confetti from "canvas-confetti";
import { RichText, isEmpty } from "@/components/rich-text";
import placeholderBadge from "@/assets/images/badges/placeholder-badge.png";
import { useMSHatIndex, usePerksAndQuirksByIds } from "@/hooks/useCMSData";
import type { BadgeView } from "@/hooks/useBadges";

type EarningEvent = BadgeView["earningEvents"][number];

type PerkQuirkReason = {
  eventId: string;
  perksQuirksId: string;
  hatId: string;
  type: "perk" | "quirk";
};

type ResolvedReason = {
  eventId: string;
  hatName: string;
  perkQuirkName: string;
  type: "perk" | "quirk";
};

// Pop-in keyframes adapted from the badge-page reveal: squash → settle.
const badgePop = keyframes`
  0%   { opacity: 0; transform: scale(0.2) rotate(-18deg); filter: blur(6px); }
  55%  { opacity: 1; transform: scale(1.18) rotate(4deg);  filter: blur(0); }
  72%  { transform: scale(0.94) rotate(-2deg); }
  85%  { transform: scale(1.06) rotate(1deg); }
  100% { transform: scale(1) rotate(0); }
`;

const riseIn = keyframes`
  0%   { opacity: 0; transform: translateY(14px); }
  100% { opacity: 1; transform: translateY(0); }
`;

// Slide the current badge away so the next one can take its place
// without the modal flashing closed/open.
const slideDownOut = keyframes`
  0%   { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(80vh); }
`;

export const BADGE_EXIT_DURATION_MS = 300;

// Hold the bare backdrop for a beat before the badge animation starts,
// so the climber registers "something special is happening" first.
const BADGE_ENTRANCE_DELAY_MS = 500;

const fireConfetti = () => {
  const canvas = document.createElement("canvas");
  canvas.style.position = "fixed";
  canvas.style.inset = "0";
  canvas.style.width = "100%";
  canvas.style.height = "100%";
  canvas.style.pointerEvents = "none";
  canvas.style.zIndex = "9999";
  document.body.appendChild(canvas);

  const myConfetti = confetti.create(canvas, { resize: true });
  const fireLeft = (count, spread) =>
    myConfetti({
      particleCount: count,
      spread,
      angle: 60,
      origin: { x: 0.25, y: 0.6 },
      disableForReducedMotion: true,
    });
  const fireRight = (count, spread) =>
    myConfetti({
      particleCount: count,
      spread,
      angle: 120,
      origin: { x: 0.75, y: 0.6 },
      disableForReducedMotion: true,
    });

  const timers: ReturnType<typeof setTimeout>[] = [];
  fireLeft(60, 70);
  timers.push(setTimeout(() => fireRight(60, 70), 150));
  timers.push(setTimeout(() => fireLeft(30, 60), 500));
  timers.push(setTimeout(() => fireRight(30, 60), 650));

  return () => {
    timers.forEach(clearTimeout);
    myConfetti.reset();
    if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
  };
};

const PERK_QUIRK_LIKED_EVENT_TYPE = "perk-quirk-badge-liked";
const MAX_REASONS = 6;

const parseEventData = (raw: unknown): Record<string, unknown> | null => {
  if (raw == null) return null;
  if (typeof raw === "object") return raw as Record<string, unknown>;
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const collectPerkQuirkReasons = (
  earningEvents: EarningEvent[] | undefined,
): PerkQuirkReason[] => {
  const seen = new Set<string>();
  const reasons: PerkQuirkReason[] = [];
  for (const event of earningEvents || []) {
    if (event?.eventType !== PERK_QUIRK_LIKED_EVENT_TYPE) continue;
    const data = parseEventData(event.data);
    if (!data) continue;
    const perksQuirksId = data.perksQuirksId
      ? String(data.perksQuirksId)
      : null;
    const hatId = data.hatId ? String(data.hatId) : null;
    if (!perksQuirksId || !hatId) continue;
    const dedupeKey = `${hatId}::${perksQuirksId}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    reasons.push({
      eventId: event.id,
      perksQuirksId,
      hatId,
      type: data.type === "quirk" ? "quirk" : "perk",
    });
  }
  return reasons;
};

type ReasonCardProps = {
  hatName?: string;
  perkQuirkName?: string;
  type: "perk" | "quirk";
};

const ReasonCard = ({ hatName, perkQuirkName, type }: ReasonCardProps) => (
  <Box
    sx={{
      backgroundColor: "rgba(255,255,255,0.05)",
      border: "1px solid rgba(145,158,171,0.2)",
      borderRadius: "14px",
      p: 2,
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
      textAlign: "left",
    }}
  >
    {hatName ? (
      <Typography
        sx={{
          fontSize: 18,
          lineHeight: "28px",
          fontWeight: 600,
          color: "rgba(255,255,255,0.8)",
        }}
      >
        {hatName}
      </Typography>
    ) : null}
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
      <Box sx={{ flexShrink: 0, lineHeight: 0, mt: "2px" }}>
        <Icon icon="mdi:thumb-up" width={20} height={20} color="#078dee" />
      </Box>
      <Typography
        sx={{
          flex: 1,
          fontSize: 16,
          lineHeight: "24px",
          fontWeight: 400,
          color: "#ffffff",
        }}
      >
        {perkQuirkName || (type === "quirk" ? "Quirk" : "Perk")}
      </Typography>
    </Box>
  </Box>
);

const formatEarnedDate = (iso?: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

type BadgeDetailContentProps = {
  badge: BadgeView | null | undefined;
  onClose: () => void;
  animateEntrance?: boolean;
  closeLabel?: string;
  phase?: "enter" | "exit";
};

export const BadgeDetailContent = ({
  badge,
  onClose,
  animateEntrance = false,
  closeLabel = "Close",
  phase = "enter",
}: BadgeDetailContentProps) => {
  // Fire confetti when the awarding-moment animation is requested. Hooks
  // must run unconditionally, so this lives above the early return.
  useEffect(() => {
    if (!animateEntrance || !badge) return undefined;
    let cleanup: (() => void) | null = null;
    const delay = setTimeout(() => {
      cleanup = fireConfetti();
    }, BADGE_ENTRANCE_DELAY_MS);
    return () => {
      clearTimeout(delay);
      if (cleanup) cleanup();
    };
  }, [animateEntrance, badge]);

  const earningEvents = badge?.earningEvents;
  const perkQuirkReasons = useMemo(
    () => collectPerkQuirkReasons(earningEvents),
    [earningEvents],
  );

  const perkQuirkIds = useMemo(
    () => perkQuirkReasons.map((r) => r.perksQuirksId),
    [perkQuirkReasons],
  );

  const { map: perkQuirkMap, isLoading: perkQuirkLoading } =
    usePerksAndQuirksByIds(perkQuirkIds);
  const { hatIndex } = useMSHatIndex();
  const hatNameById = useMemo(() => {
    const next = new Map<string, string>();
    for (const hat of hatIndex || []) {
      if (hat?.id) next.set(String(hat.id), hat.displayName || "");
    }
    return next;
  }, [hatIndex]);

  const resolvedReasons = useMemo<ResolvedReason[]>(() => {
    return perkQuirkReasons
      .map((r): ResolvedReason | null => {
        const pq = perkQuirkMap.get(r.perksQuirksId);
        if (!pq) return null;
        const hatName = hatNameById.get(r.hatId) || "";
        return {
          eventId: r.eventId,
          hatName,
          perkQuirkName: pq.name || "",
          type: r.type,
        };
      })
      .filter((r): r is ResolvedReason => r !== null);
  }, [perkQuirkReasons, perkQuirkMap, hatNameById]);

  if (!badge) return null;

  const { name, iconUrl, iconAlt, earned, earnedAt, description } = badge;
  const imgSrc = iconUrl || placeholderBadge;
  const earnedLabel = earned ? formatEarnedDate(earnedAt) : null;
  // BadgeView.description is `unknown` (raw CMS rich-text JSON). Narrow at
  // the rendering boundary so the RichText component sees a typed value.
  const richDescription = description as Parameters<typeof isEmpty>[0];
  const hasDescription = richDescription != null && !isEmpty(richDescription);

  const visibleReasons = resolvedReasons.slice(0, MAX_REASONS);
  const overflowCount = Math.max(
    0,
    resolvedReasons.length - visibleReasons.length,
  );
  const showReasonSkeleton =
    perkQuirkReasons.length > 0 &&
    resolvedReasons.length === 0 &&
    perkQuirkLoading;

  const D = BADGE_ENTRANCE_DELAY_MS;
  const riseAnim = (offsetMs: number) =>
    animateEntrance
      ? {
          animation: `${riseIn} 500ms cubic-bezier(.2,.9,.3,1.2) ${offsetMs + D}ms both`,
        }
      : null;
  const headingAnim = riseAnim(1000);
  const titleAnim = riseAnim(1100);
  const earnedAnim = riseAnim(1220);
  const descAnim = riseAnim(1400);
  const reasonsAnim = riseAnim(1520);
  const ctaAnim = riseAnim(1640);
  const imgAnim = animateEntrance
    ? { animation: `${badgePop} 1100ms cubic-bezier(.2,.9,.3,1.2) ${D}ms both` }
    : null;

  return (
    <Box
      sx={{
        width: { xs: "92vw", sm: 600, md: 732, lg: 1000 },
        maxWidth: "92vw",
        textAlign: "center",
        color: "#ffffff",
        p: 4,
      }}
    >
      <Box
        key={badge.id}
        sx={
          phase === "exit"
            ? {
                animation: `${slideDownOut} ${BADGE_EXIT_DURATION_MS}ms cubic-bezier(.4,.0,.6,1) both`,
              }
            : null
        }
      >
        {/* Earning-moment only: the "BADGE EARNED" heading distinguishes the
            celebration from the read-only badge detail view. */}
        {animateEntrance ? (
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 18,
              letterSpacing: "0em",
              textTransform: "uppercase",
              color: "#ffffff",
              fontFamily: "ProximaNova Bold",
              mb: 1,
              ...headingAnim,
            }}
          >
            Badge Earned
          </Typography>
        ) : null}
        <Typography
          id="badge-detail-title"
          variant="h3"
          sx={{ fontWeight: 700, color: "#ffffff", mb: 1, ...titleAnim }}
        >
          {name}
        </Typography>
        {earnedLabel ? (
          <Typography
            variant="body2"
            sx={{
              fontStyle: "italic",
              color: "rgba(255,255,255,0.85)",
              mb: 3,
              ...earnedAnim,
            }}
          >
            Earned {earnedLabel}
          </Typography>
        ) : (
          <Box sx={{ mb: 3 }} />
        )}
        <Box
          component="img"
          src={imgSrc}
          alt={iconAlt || name}
          sx={{
            width: 200,
            height: 200,
            objectFit: "contain",
            filter: earned ? "none" : "grayscale(0.7)",
            mb: 3,
            ...imgAnim,
          }}
        />
        {hasDescription ? (
          <Box
            sx={{
              color: "#ffffff",
              fontWeight: 700,
              mb: 3,
              "& p": { m: 0 },
              ...descAnim,
            }}
          >
            <RichText value={richDescription} />
          </Box>
        ) : null}
        {visibleReasons.length > 0 ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(3, minmax(0, 1fr))",
              },
              gap: 1.5,
              mb: 3,
              ...reasonsAnim,
            }}
          >
            {visibleReasons.map((reason) => (
              <ReasonCard
                key={reason.eventId}
                hatName={reason.hatName}
                perkQuirkName={reason.perkQuirkName}
                type={reason.type}
              />
            ))}
            {overflowCount > 0 ? (
              <Typography
                variant="caption"
                sx={{
                  gridColumn: "1 / -1",
                  color: "rgba(255,255,255,0.7)",
                  textAlign: "center",
                }}
              >
                and {overflowCount} more
              </Typography>
            ) : null}
          </Box>
        ) : showReasonSkeleton ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(3, minmax(0, 1fr))",
              },
              gap: 1.5,
              mb: 3,
              ...reasonsAnim,
            }}
          >
            {perkQuirkReasons.slice(0, MAX_REASONS).map((r) => (
              <Skeleton
                key={r.eventId}
                variant="rounded"
                height={114}
                sx={{ bgcolor: "rgba(255,255,255,0.12)" }}
              />
            ))}
          </Box>
        ) : null}
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            backgroundColor: "#1F8CDC",
            color: "#ffffff",
            fontWeight: 700,
            px: 4,
            borderRadius: 1,
            "&:hover": { backgroundColor: "#1976c0" },
            ...ctaAnim,
          }}
        >
          {closeLabel}
        </Button>
      </Box>
    </Box>
  );
};

type BadgeDetailModalProps = {
  badge: BadgeView | null | undefined;
  open: boolean;
  onClose: () => void;
};

// Modal wrapper for the badges-grid "click a badge to inspect" use case.
// Celebrations render BadgeDetailContent directly inside the
// CelebrationCoordinator's shade.
const BadgeDetailModal = ({ badge, open, onClose }: BadgeDetailModalProps) => (
  <Modal
    open={open}
    onClose={onClose}
    aria-labelledby="badge-detail-title"
    slotProps={{
      backdrop: {
        sx: {
          backgroundColor: "rgba(20, 5, 35, 0.55)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
        },
      },
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        outline: "none",
      }}
    >
      <BadgeDetailContent badge={badge} onClose={onClose} />
    </Box>
  </Modal>
);

export default BadgeDetailModal;
